/**
 * Cola de envío offline para el formulario público (PWA). Guarda cada
 * respuesta en IndexedDB antes de intentar enviarla; si no hay red, queda
 * pendiente y se reintenta al reconectar o al reabrir la app. Así "Guardada,
 * se enviará sola" (§4.1 HU-1.8) es literal, no solo un mensaje.
 */
const DB_NOMBRE = 'rumbo-cola'
const ALMACEN = 'respuestas'

function abrirDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NOMBRE, 1)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(ALMACEN)) db.createObjectStore(ALMACEN, { keyPath: 'id' })
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

export interface ElementoCola {
  id: string
  token: string
  datos: unknown
  folio?: string
  estado: 'pendiente' | 'enviada' | 'error'
  intentos: number
  guardadoEn: string
  error?: string
}

async function conAlmacen<T>(modo: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | undefined): Promise<T> {
  const db = await abrirDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ALMACEN, modo)
    const req = fn(tx.objectStore(ALMACEN))
    tx.oncomplete = () => resolve(req?.result as T)
    tx.onerror = () => reject(tx.error)
  })
}

export async function encolar(item: ElementoCola): Promise<void> {
  await conAlmacen('readwrite', (s) => s.put(item))
}

export async function listarCola(): Promise<ElementoCola[]> {
  return conAlmacen<ElementoCola[]>('readonly', (s) => s.getAll())
}

export async function actualizarCola(id: string, cambios: Partial<ElementoCola>): Promise<void> {
  const db = await abrirDB()
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(ALMACEN, 'readwrite')
    const store = tx.objectStore(ALMACEN)
    const get = store.get(id)
    get.onsuccess = () => {
      if (get.result) store.put({ ...get.result, ...cambios })
    }
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

export async function quitarDeCola(id: string): Promise<void> {
  await conAlmacen('readwrite', (s) => s.delete(id))
}

/** Intenta enviar cada pendiente; se llama al recuperar red y al iniciar la app. */
export async function procesarCola(enviar: (token: string, datos: unknown) => Promise<{ folio: string; id: string }>): Promise<void> {
  const pendientes = (await listarCola()).filter((x) => x.estado === 'pendiente')
  for (const item of pendientes) {
    try {
      const r = await enviar(item.token, item.datos)
      await actualizarCola(item.id, { estado: 'enviada', folio: r.folio })
    } catch (e) {
      await actualizarCola(item.id, { intentos: item.intentos + 1, error: (e as Error).message })
    }
  }
}
